// client/src/components/common/ThemedTimePicker.tsx (converted from .js, ST-00204)
//
// A time of day ("HH:MM") for forms: quiet hours, report and schedule times, geofence windows.
// onChange fires a native-looking `{ target: { value: 'HH:MM' } }`.
//
// One button showing the time, opening the time chooser (PickerTimeChooser, ST-00264) with
// Cancel / Apply: every hour as a target, then the minutes on the 5-minute grid, and "Type a
// time" for any other minute — no tiny menus, no ± tapping. The page's quick times, if it gives
// any, take their time of day. Nothing reaches the page until Apply.
//
// Desktop (ST-00294): the button is 36 px tall like the pair of hour / minute selects it
// replaces, and opens a popup under it (PickerPopover) with "Type a time" focused — typing 1435
// and Enter is the whole job — and a wide, short chooser (hours 00–11 / 12–23 in two rows, the
// minutes in one) that fits a laptop screen whole. Phone / touch (ST-00204): a 44 px button
// opening a bottom sheet that starts on the hour grid (six across).

import { useRef, useState } from 'react';
import PickerSurface from './PickerSurface';
import PickerTimeChooser from './PickerTimeChooser';
import PickerTimePresets from './PickerTimePresets';
import { usePickerSheetMode } from './pickerHooks';
import { formatHm, parseHm, timeOf, type HourMinute, type TimePreset } from './pickerLogic';

export interface ThemedTimePickerProps {
  /** "HH:MM"; defaults to 07:00. */
  value?: string;
  onChange?: (e: { target: { value: string } }) => void;
  disabled?: boolean;
  className?: string;
  /** Names the time (popup heading, button label), e.g. "Quiet hours start". */
  label?: string;
  /** Quick-time chips (their time of day is taken; none by default). */
  presets?: readonly TimePreset[];
}

const ClockIcon = () => (
  <svg className="picker-field__icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="10" cy="10" r="7.25" stroke="currentColor" strokeWidth="1.5" />
    <path d="M10 6v4.25l2.75 1.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ThemedTimePicker = ({
  value = '07:00',
  onChange,
  disabled = false,
  className = '',
  label,
  presets,
}: ThemedTimePickerProps) => {
  const sheetMode = usePickerSheetMode();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const hourRef = useRef<HTMLButtonElement | null>(null);
  const typedRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<HourMinute>({ h: 7, m: 0 });
  const [typedInvalid, setTypedInvalid] = useState(false);

  const current = parseHm(value || '07:00');
  const shown = formatHm(current);
  const surfaceOpen = open && !disabled;

  const apply = () => {
    if (typedInvalid) return;
    onChange?.({ target: { value: formatHm(pending) } });
    setOpen(false);
  };

  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      className={sheetMode ? 'picker-field__main' : 'picker-time-trigger'}
      disabled={disabled}
      aria-haspopup="dialog"
      aria-expanded={surfaceOpen}
      aria-label={`${label ?? 'Time'}: ${shown}. Change the time`}
      onClick={() => {
        setPending(current);
        setOpen(true);
      }}
    >
      <span className={sheetMode ? 'picker-field__text picker-field__text--time' : 'picker-time-trigger__text'}>
        {shown}
      </span>
      <ClockIcon />
    </button>
  );

  return (
    <div
      className={
        sheetMode
          ? `picker-field picker-field--time${disabled ? ' picker-field--disabled' : ''} ${className}`
          : `picker-time-field ${className}`
      }
    >
      {trigger}
      <PickerSurface
        variant={sheetMode ? 'sheet' : 'popover'}
        open={surfaceOpen}
        title={label ?? 'Choose a time'}
        onCancel={() => setOpen(false)}
        onApply={apply}
        applyDisabled={typedInvalid}
        returnFocusRef={triggerRef}
        initialFocusRef={sheetMode ? hourRef : typedRef}
      >
        {presets && presets.length > 0 && (
          <PickerTimePresets
            presets={presets}
            value={new Date(2000, 0, 1, pending.h, pending.m)}
            timeOnly
            onPick={(at) => setPending(timeOf(at))}
          />
        )}
        <PickerTimeChooser
          label={label ?? 'Time'}
          value={pending}
          onChange={setPending}
          hourRef={hourRef}
          typedRef={typedRef}
          onTypedInvalidChange={setTypedInvalid}
          onEnter={sheetMode ? undefined : apply}
          // Desktop: wide and short (two rows of hours, one of minutes), no scrolling.
          wide={!sheetMode}
        />
      </PickerSurface>
    </div>
  );
};

export default ThemedTimePicker;
